import { PhoneNumber } from 'libphonenumber-js';
import parseMax from 'libphonenumber-js/max';
import { IAppState, IFormControl } from '../types';
import { validationCodes } from '../constants';
import { InputTypes } from '../types/InputTypes';
import { IValidationCode } from '../types/IValidationCode';
import { executeUiValidation } from './validations/executeUiValidation';
import { IValidationError } from '../types/IValidationError';
import { validateIban } from './validations/iban-validator';

export const getErrorByCode = (
    validations: IValidationCode[],
    code: string,
    requiredMessage: string,
) => {
    const codeItem = Array.isArray(validations) && validations.find((c) => c.code === code);
    let messageText = codeItem ? codeItem.message : '';

    if (!codeItem) {
        messageText = code === validationCodes.required && requiredMessage ? requiredMessage : code;
    }

    return messageText;
};

export const validatePhoneNumber = (ctrl: IFormControl, appState: IAppState) => {
    const control = ctrl;
    const { value, countryCode } = control;
    let phone: PhoneNumber | undefined;

    if (countryCode) {
        phone = parseMax(value, countryCode);
        control.isValid = phone
            ? phone.isValid() && phone.country?.toLowerCase() === countryCode
            : false;

        if (control.isValid) {
            control.value = phone?.number;
            return;
        }
    }

    phone = phone ?? parseMax(value);
    control.isValid = phone ? phone.isValid() : false;

    if (!control.isValid) {
        control.validationMessage = getErrorByCode(
            control.validations ?? [],
            validationCodes.invalid,
            appState.formMeta.requiredMessage,
        );
    }
};

export const validateEmail = (email: string) => {
    // eslint-disable-next-line no-useless-escape
    const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
};

export const validateLenderAmounts = (appState: IAppState) => {
    const controlsState = appState.controlsState as any;
    const controls = Object.values(controlsState) as any[];
    const lenderAmount = '.lenderAmount';
    const lenderItems = 'refinData.lenderItems';
    let sum = 0;

    if ((controls.reduce((acc: any, ctrl: IFormControl) => {
        if (ctrl.controlName?.includes(lenderAmount)) {
            sum = ctrl.value ? sum + Number(ctrl.value) : sum;
        }

        return sum;
    })) > appState.controlsState.amount?.value) {
        controls.forEach((ctrl: IFormControl) => {
            if (ctrl.controlName === lenderItems) {
                ctrl.isValid = false;
            }

            if (ctrl.controlName?.includes(lenderAmount)) {
                ctrl.isValid = false;
                ctrl.validationMessage = getErrorByCode(
                    ctrl.validations || [],
                    validationCodes.invalid,
                    validationCodes.invalid,
                )
            }
        })
    }
    else {
        controls.forEach((ctrl: IFormControl) => {
            if (ctrl.controlName === lenderItems) ctrl.isValid = true;
            if (ctrl.controlName?.includes(lenderAmount) && ctrl.value > 0) ctrl.isValid = true;
        })
    }
};

export const isValidationError = (error: any): error is IValidationError =>
    typeof error === 'object' && 'errorType' in error;

export const hasValue = (type: InputTypes, value: any) => {
    if (type === 'checkbox' || type === 'switch') {
        return value as boolean;
    }

    if (type === 'documentUpload') {
        return value.length as boolean;
    }

    return value !== undefined && value !== null && value !== '';
};

export const validateController = (ctrl: IFormControl, appState: IAppState) => {
    const control = ctrl;
    const { requiredMessage } = appState.formMeta;
    const { parentProps, value, type, required, validations, disabled } = control;

    if (disabled || (parentProps && parentProps.disabled)) {
        control.isValid = true;
        return;
    }

    control.isValid = true;

    if (required && !hasValue(type, value)) {
        control.isValid = false;
        control.validationMessage = getErrorByCode(
            validations || [],
            validationCodes.required,
            requiredMessage,
        );
    }

    if ((required || hasValue(type, value)) && control.isValid && type === 'phoneNumber') {
        validatePhoneNumber(control, appState);
    }

    if (control.isValid && control.controlName?.includes('.lenderAmount')) {
        validateLenderAmounts(appState);
    }

    if (control.isValid && control.controlName?.includes('.lenderIban')) {
        validateIban(control, appState);
    }

    if ((required || hasValue(type, value)) && control.specificValidation) {
        control.specificValidation(control, appState);
    }

    executeUiValidation(control, appState);
};
